import { Check } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ConceptSuggestion } from "@/types/concept";

interface Props {
  proposal: ConceptSuggestion;
  refinementOptions?: string[];
  onAccept: (proposal: ConceptSuggestion) => void;
  onRefine: (correction: string) => void;
  disabled?: boolean;
}

export function ConceptProposalCard({
  proposal,
  refinementOptions,
  onAccept,
  onRefine,
  disabled = false,
}: Props) {
  const [showRefine, setShowRefine] = useState(false);
  const [customCorrection, setCustomCorrection] = useState("");

  return (
    <div className="grid gap-3">
      {/* Proposal card */}
      <div className="rounded-lg border border-green-600/50 bg-muted/50 p-4">
        <div className="mb-1 text-xs uppercase tracking-wide text-green-500">
          Proposed concept
        </div>
        <div className="mb-1.5 text-base font-bold">{proposal.name}</div>
        <div className="mb-2 text-sm leading-relaxed text-muted-foreground">
          {proposal.abstract}
        </div>
        <div className="flex flex-wrap gap-1">
          {proposal.keywords.map((kw) => (
            <span
              key={kw}
              className="rounded-full bg-background px-2 py-0.5 text-[11px] text-blue-400"
            >
              {kw}
            </span>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => onAccept(proposal)}
          disabled={disabled}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          <Check className="mr-1 size-3.5" />
          Use this concept
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowRefine(!showRefine)}
          disabled={disabled}
          className="flex-1"
        >
          Not quite &rarr; refine
        </Button>
      </div>

      {/* "Not quite" refinement panel */}
      {showRefine && (
        <div className="rounded-lg border border-dashed border-border p-3">
          <div className="mb-1.5 text-xs text-muted-foreground">
            What&rsquo;s different about your focus?
          </div>
          {refinementOptions && refinementOptions.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {refinementOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onRefine(opt)}
                  disabled={disabled}
                  className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs transition-colors hover:border-primary/50"
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-1.5">
            <Input
              value={customCorrection}
              onChange={(e) => setCustomCorrection(e.target.value)}
              placeholder="Or describe the difference..."
              className="h-7 text-xs"
              disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter" && customCorrection.trim()) {
                  onRefine(customCorrection.trim());
                }
              }}
            />
            <Button
              size="sm"
              onClick={() => {
                if (customCorrection.trim()) onRefine(customCorrection.trim());
              }}
              disabled={disabled || !customCorrection.trim()}
              className="h-7 shrink-0 text-xs"
            >
              Refine &rarr;
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
